import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Dropdown, Label } from '@heroui/react';
import { DataGrid, NumberValue, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { formatInLanguage } from '@/utils/dateLocale';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { InvoiceTotal } from '../invoiceTotal';
import {
    PENDING_INVOICE_ROW_ACTIONS,
    formatInvoiceNumber,
    type PendingInvoice,
    type PendingInvoiceRowAction,
} from './pendingInvoicesMeta';

type PendingInvoicesViewProps = {
    items: PendingInvoice[];
    onImport: (invoiceId: string) => void;
};

/**
 * Incoming-invoices table for the Purchases page. Mirrors the structure
 * of `TransactionsView` (Widget shell with a DataGrid inside) so the
 * two tables share visual rhythm.
 *
 * Every column is sortable (client-side via `accessorKey`), and the
 * trailing `actions` column is `pinned: "end"` so the shared ellipsis
 * dropdown (hosting the Import action) stays visible during horizontal
 * scroll — matching the Categories / Suppliers tables.
 */
export const PendingInvoicesView: React.FC<PendingInvoicesViewProps> = ({
    items,
    onImport,
}) => {
    const { t, i18n } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            title: t('purchases.widgets.pendingInvoices.title'),
            subtitle: (count: number) =>
                t('purchases.widgets.pendingInvoices.subtitle', { count }),
            tableAria: t('purchases.widgets.pendingInvoices.tableAria'),
            actionsAria: t('purchases.widgets.pendingInvoices.actionsAria'),
            columns: {
                invoice: t('purchases.widgets.pendingInvoices.columns.invoice'),
                supplier: t(
                    'purchases.widgets.pendingInvoices.columns.supplier',
                ),
                date: t('purchases.widgets.pendingInvoices.columns.date'),
                productCount: t(
                    'purchases.widgets.pendingInvoices.columns.productCount',
                ),
                totalAmount: t(
                    'purchases.widgets.pendingInvoices.columns.totalAmount',
                ),
            },
            empty: {
                title: t('purchases.widgets.pendingInvoices.empty.title'),
            },
        }),
        [t],
    );

    const onRowAction = React.useCallback(
        (invoiceId: string, action: PendingInvoiceRowAction) => {
            if (action === 'import') {
                onImport(invoiceId);
            }
        },
        [onImport],
    );

    const columns = React.useMemo<DataGridColumn<PendingInvoice>[]>(
        () => [
            {
                id: 'invoice',
                header: copy.columns.invoice,
                isRowHeader: true,
                width: 150,
                accessorKey: 'invoiceNumber',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="font-mono text-xs font-bold text-accent"
                        {...getNodeTestIdProp(
                            `pending-invoices-row-number-${item.id}`,
                        )}
                    >
                        {formatInvoiceNumber(item.invoiceNumber)}
                    </span>
                ),
            },
            {
                id: 'supplier',
                header: copy.columns.supplier,
                accessorKey: 'supplier',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="truncate text-sm text-foreground"
                        {...getNodeTestIdProp(
                            `pending-invoices-row-supplier-${item.id}`,
                        )}
                    >
                        {item.supplier}
                    </span>
                ),
            },
            {
                id: 'date',
                header: copy.columns.date,
                width: 150,
                accessorKey: 'date',
                allowsSorting: true,
                cell: (item) => (
                    <span
                        className="text-xs text-muted"
                        {...getNodeTestIdProp(
                            `pending-invoices-row-date-${item.id}`,
                        )}
                    >
                        {formatInLanguage(
                            item.date,
                            'D MMM YYYY · HH:mm',
                            i18n.language,
                        )}
                    </span>
                ),
            },
            {
                id: 'productCount',
                header: copy.columns.productCount,
                width: 110,
                align: 'end',
                accessorKey: 'productCount',
                allowsSorting: true,
                cell: (item) => (
                    <NumberValue
                        className="text-sm tabular-nums text-foreground"
                        maximumFractionDigits={0}
                        minimumFractionDigits={0}
                        style="decimal"
                        value={item.productCount}
                        {...getNodeTestIdProp(
                            `pending-invoices-row-product-count-${item.id}`,
                        )}
                    />
                ),
            },
            {
                id: 'totalAmount',
                header: copy.columns.totalAmount,
                width: 140,
                align: 'end',
                accessorKey: 'totalAmount',
                allowsSorting: true,
                cell: (item) => (
                    <InvoiceTotal
                        expensesTotalTetri={item.expensesTotalTetri}
                        linesTotalTetri={item.linesTotalTetri}
                        rowId={item.id}
                        slot="pending-invoices-row"
                        totalAmountTetri={item.totalAmountTetri}
                    />
                ),
            },
            {
                id: 'actions',
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => (
                    <div
                        className="flex justify-center"
                        {...getNodeTestIdProp(
                            `pending-invoices-row-actions-${item.id}`,
                        )}
                    >
                        <Dropdown>
                            <Button
                                aria-label={copy.actionsAria}
                                isIconOnly
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    `pending-invoices-row-actions-trigger-${item.id}`,
                                )}
                            >
                                <Icon icon="gravity-ui:ellipsis-vertical" />
                            </Button>
                            <Dropdown.Popover className="min-w-[160px]">
                                <Dropdown.Menu
                                    onAction={(key) =>
                                        onRowAction(
                                            item.id,
                                            key as PendingInvoiceRowAction,
                                        )
                                    }
                                >
                                    {PENDING_INVOICE_ROW_ACTIONS.map(
                                        (action) => (
                                            <Dropdown.Item
                                                id={action.id}
                                                key={action.id}
                                                textValue={t(action.labelKey)}
                                                variant={action.variant}
                                                {...getNodeTestIdProp(
                                                    `pending-invoices-row-action-${action.id}-${item.id}`,
                                                )}
                                            >
                                                <Icon icon={action.icon} />
                                                <Label>
                                                    {t(action.labelKey)}
                                                </Label>
                                            </Dropdown.Item>
                                        ),
                                    )}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown>
                    </div>
                ),
            },
        ],
        [copy, i18n.language, onRowAction, t],
    );

    return (
        <section
            aria-labelledby="pending-invoices-widget-title"
            {...getNodeTestIdProp('pending-invoices-section')}
        >
            <Widget
                className="flex flex-col gap-0 overflow-hidden p-0"
                {...getNodeTestIdProp('pending-invoices-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('pending-invoices-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('pending-invoices-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"
                            {...getNodeTestIdProp('pending-invoices-icon-wrap')}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:inbox-in-bold-duotone"
                                {...getNodeTestIdProp('pending-invoices-icon')}
                            />
                        </div>
                        {copy.title}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'pending-invoices-widget-subtitle',
                        )}
                    >
                        {copy.subtitle(items.length)}
                    </Widget.Description>
                </Widget.Header>
                <DataGrid<PendingInvoice>
                    aria-label={copy.tableAria}
                    columns={columns}
                    data={items}
                    defaultSortDescriptor={{
                        column: 'date',
                        direction: 'descending',
                    }}
                    getRowId={(item) => item.id}
                    renderEmptyState={() => (
                        <div
                            className="flex flex-col items-center gap-3 py-12 text-muted"
                            {...getNodeTestIdProp('pending-invoices-empty')}
                        >
                            <Icon
                                aria-hidden
                                className="size-8 text-default-500"
                                icon="solar:inbox-in-bold-duotone"
                                {...getNodeTestIdProp(
                                    'pending-invoices-empty-icon',
                                )}
                            />
                            <span
                                className="text-sm"
                                {...getNodeTestIdProp(
                                    'pending-invoices-empty-title',
                                )}
                            >
                                {copy.empty.title}
                            </span>
                        </div>
                    )}
                    scrollContainerClassName="max-h-[520px] overflow-y-auto"
                    variant="primary"
                    {...getNodeTestIdProp('pending-invoices-grid')}
                />
            </Widget>
        </section>
    );
};
